import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const featuredTrucks = { featured: true };

function FoodTruckRental() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type} Rental in ${city_name}`}
        type='website'
        canonical='/food-truck-rental'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`Rent a food ${vehicleType} for your next event in ${city_name}. Weddings, corporate events, birthday parties, and more. Get free quotes from ${city_name}'s top food ${vehicleType}s.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Rental in ${city_name}`,
            description: `Rent a food ${vehicleType} for your next event in ${city_name}. Weddings, corporate events, birthday parties, and more.`,
            url: '/food-truck-rental',
            serviceType: 'Food Truck Rental',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Food Truck Rental', item: '/food-truck-rental' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Rent a Food {vehicle_type} for Your Event in {city_name}</h1>
            <p>Looking to rent a food {vehicleType} for a party, corporate event, or special occasion? {site_title} connects you with {city_name}&#39;s best food {vehicleType}s available for rental. Whether you&#39;re planning a wedding, a company outing, or a neighborhood block party, a food {vehicleType} rental brings fresh, made-to-order food right to your guests. Browse top-rated {vehicleType}s, compare menus, and book the perfect rental for your event.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Get a Free Quote</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Popular Food ${vehicle_type}s for Rent`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Rent a Food ${vehicle_type} for Your Event?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Food {vehicle_type} Rentals for Every Occasion</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Private Parties & Celebrations</h3>
            <p>Rent a food {vehicleType} to make your next party unforgettable. Birthday parties, graduation celebrations, anniversaries, and backyard gatherings are all elevated when guests can order fresh meals from a {vehicleType}. Skip the stress of cooking for a crowd and let a professional {vehicleType} handle everything from setup to cleanup.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Corporate Events & Team Outings</h3>
            <p>Food {vehicleType} rentals are one of the most popular choices for corporate events in {city_name}. From employee appreciation lunches to company picnics and product launches, a rented food {vehicleType} provides a unique dining experience your team will love. Many {vehicleType}s offer customizable menus to fit your group size and dietary needs.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/community.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Weddings & Special Events</h3>
            <p>A food {vehicleType} rental adds a fun, memorable twist to your wedding reception or rehearsal dinner. Guests love the casual vibe and variety that {vehicleType}s bring. Whether you want tacos, BBQ, wood-fired pizza, or gourmet sliders, {city_name} has a food {vehicleType} to match your vision and budget.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Rent a Food ${vehicle_type} for Your Next Event!`} />
      </section>
    </div>
  );
}

export default FoodTruckRental;
